import type { AxiosRequestConfig } from "axios";
import { API_URLS } from "@/config/urls";
import { handleAxiosError, isAxiosError } from "@/lib/apiErrors";
import { api } from "@/lib/axios";
import {
  type MentionUsersPage,
  normalizeMentionUsersPage,
} from "@/lib/humhub-api/mentionUsers";
import type {
  InvitableUser,
  PaginatedResponse,
  StudyGroupInviteLinkAcceptResponse,
  StudyGroupInviteLinkCreateResponse,
  StudyGroupInviteLinkInfoResponse,
  StudyGroupInviteLinksRevokeResponse,
  StudyGroupMembershipResponse,
  StudyGroupYourMembershipResponse,
} from "@/lib/humhub-api/types";

type PublicInviteLinkRequestConfig = AxiosRequestConfig & {
  skipAuthFlow: true;
  omitAuthCredentials: true;
};

export const studyGroupMembershipApi = {
  getStudyGroupMemberships: async (
    id: string | number,
    perPage = 20,
    page = 1,
    keyword?: string,
    membershipStatus?: number,
  ): Promise<PaginatedResponse<StudyGroupMembershipResponse>> => {
    try {
      const response = await api.get<
        PaginatedResponse<StudyGroupMembershipResponse>
      >(`${API_URLS.STUDY_GROUP}/${id}/membership/find-by-membership-status`, {
        params: {
          limit: perPage,
          page,
          keyword,
          membershipStatus,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching study group memberships",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getStudyGroupYourMembership: async (
    studyGroupId: string | number,
  ): Promise<StudyGroupYourMembershipResponse> => {
    try {
      const response = await api.get<StudyGroupYourMembershipResponse>(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/current-user`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching your study group membership",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getAdmins: async (
    studyGroupId: string | number,
    perPage = 20,
    page = 1,
  ): Promise<PaginatedResponse<StudyGroupMembershipResponse>> => {
    try {
      const response = await api.get<
        PaginatedResponse<StudyGroupMembershipResponse>
      >(`${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/admins`, {
        params: {
          limit: perPage,
          page,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching study group admins",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getBlockedUsers: async (
    studyGroupId: string | number,
    perPage = 20,
    page = 1,
  ): Promise<PaginatedResponse<StudyGroupMembershipResponse>> => {
    try {
      const response = await api.get<
        PaginatedResponse<StudyGroupMembershipResponse>
      >(`${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/blocked-users`, {
        params: {
          limit: perPage,
          page,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching blocked study group users",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getInvitableUsers: async (
    studyGroupId: string | number,
    keyword?: string,
  ): Promise<PaginatedResponse<InvitableUser>> => {
    try {
      const response = await api.get(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/invitable-users`,
        {
          params: keyword ? { keyword } : {},
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching invitable users",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  createInviteLink: async (
    studyGroupId: string | number,
  ): Promise<StudyGroupInviteLinkCreateResponse> => {
    try {
      const response = await api.post<StudyGroupInviteLinkCreateResponse>(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/invite-link`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while creating the study group invite link",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  revokeInviteLinks: async (
    studyGroupId: string | number,
  ): Promise<StudyGroupInviteLinksRevokeResponse> => {
    try {
      const response = await api.delete<StudyGroupInviteLinksRevokeResponse>(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/invite-links`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while revoking study group invite links",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getInviteLink: async (
    token: string,
  ): Promise<StudyGroupInviteLinkInfoResponse> => {
    try {
      const response = await api.get<StudyGroupInviteLinkInfoResponse>(
        `${API_URLS.STUDY_GROUP}/invite-link/${encodeURIComponent(token)}`,
        {
          skipAuthFlow: true,
          omitAuthCredentials: true,
        } as PublicInviteLinkRequestConfig,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching the study group invite link",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  acceptInviteLink: async (
    token: string,
  ): Promise<StudyGroupInviteLinkAcceptResponse> => {
    try {
      const response = await api.post<StudyGroupInviteLinkAcceptResponse>(
        `${API_URLS.STUDY_GROUP}/invite-link/${encodeURIComponent(token)}/accept`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while accepting the study group invite link",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getMentionAvailableUsers: async (
    studyGroupId: string | number,
    params?: {
      keyword?: string;
      topicIds?: Array<string | number>;
      page?: number;
      limit?: number;
    },
  ): Promise<MentionUsersPage> => {
    try {
      const response = await api.get(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/filter-mention-available-users`,
        {
          params: {
            ...(params?.keyword ? { keyword: params.keyword } : {}),
            ...(params?.topicIds?.length
              ? { topic_ids: params.topicIds.join(",") }
              : {}),
            page: params?.page ?? 1,
            limit: params?.limit ?? 10,
          },
        },
      );
      return normalizeMentionUsersPage(response.data, {
        page: params?.page ?? 1,
        limit: params?.limit ?? 10,
      });
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching mention available users",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  updateMemberRole: async (
    studyGroupId: string | number,
    userId: string | number,
    role: "admin" | "moderator" | "member",
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.put(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/${userId}/role`,
        { role },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while updating the member role",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  transferOwner: async (
    studyGroupId: string | number,
    userId: string | number,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.put(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/owner`,
        { userId },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while transferring the study group owner",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
};
